/**
 * 2D floor-plan glyphs for door/window openings.
 *
 * Given an opening's horizontal {@link OpeningFrame} (wall axis, normal, width)
 * plus its kind, wall thickness and swing, produce flat line-segment positions
 * `[x1,y1,z, x2,y2,z, ...]` drawing the standard architectural symbol inside
 * the wall gap the cutout punched:
 *  - WINDOW: parallel glass/frame lines spanning the opening within the wall
 *    thickness.
 *  - DOOR: a leaf line (the door panel) from the hinge plus a swing arc.
 *
 * All geometry is z-flat at the supplied `z`, matching the wall fill, and lives
 * in the same world XY space as the wall outline so it renders through the same
 * LineSegments2 path.
 */

import { OpeningKind, DoorType, isCustomOpeningTypeId, type DoorGlyphType, type ObjectDefinitionSpec, type ObjectParamValue, type ObjectShapeLayer, type WindowGlyphType } from 'shared-types';
import type { OpeningFrame, Pt2 } from '../FloorMesh3DBuilder';
import { getCustomOpeningType } from '../../sketchObjects/custom/openings/customOpeningRegistry';
import { evaluateCustomOpeningLocal, mirrorOpeningSegments } from '../../sketchObjects/custom/openings/evaluateCustomOpening';
import { isBentOpeningFrame, localOpeningFrame, placeOpeningSegments } from './openingWallMap';

export interface OpeningGlyphInput {
  frame: OpeningFrame;
  kind: OpeningKind;
  /** Wall thickness at the opening, in meters — sets the symbol's cross depth. */
  wallThickness: number;
  /** Door hinge at the start (−) end of the wall axis when true, else the end (+). */
  hingeAtStart: boolean;
  /** Door swings/opens toward +wallNorm when true, else −wallNorm. */
  swingPositive: boolean;
  /** DOOR only: which floor-plan door symbol to draw (built-in or an organisation door). */
  doorType: DoorGlyphType;
  /** WINDOW only: the built-in window (default) or an organisation window. */
  windowType?: WindowGlyphType;
  /** WINDOW only: render as a rollable (roller-shutter) window. */
  rollable: boolean;
  /** Organisation door / window: the opening's param values (missing → defaults). */
  customParams?: Readonly<Record<string, ObjectParamValue>>;
  /** Opening height (m) — organisation doors / windows can read it. */
  height?: number;
}

/** The organisation door / window type an opening draws, if any. */
export function customGlyphTypeOf(input: Pick<OpeningGlyphInput, 'kind' | 'doorType' | 'windowType'>): string | null {
  const type = input.kind === OpeningKind.WINDOW ? input.windowType : input.doorType;
  return type && isCustomOpeningTypeId(type) ? type : null;
}

/** Segments approximating a quarter-circle arc, for the door swing. */
const ARC_SEGMENTS = 12;

/**
 * A true circular arc in the glyph, recorded alongside its tessellated chord
 * segments. Rendering and pixel hit-testing keep consuming the flat segments;
 * arc-capable exports (DXF bulge) emit this instead and skip the covered
 * chords. `bulge` uses the INTERNAL sign convention (see arcGeometry:
 * positive bows left of start→end). [segStart, segEnd) is the flat-array
 * index range of the chord segments this arc replaces.
 */
export interface OpeningGlyphArc {
  start: Pt2;
  end: Pt2;
  z: number;
  bulge: number;
  segStart: number;
  segEnd: number;
}

/**
 * Half-separation of the window's two glass lines, in meters. The pair sits at
 * ±this around the wall centreline, so the glass symbol is ~2× this thick (a
 * thin frame regardless of wall thickness).
 */
const WINDOW_HALF_THICKNESS = 0.025; // 5 cm total

/** Depth of the roller-shutter box of a rollable window, in meters (on the
 * exterior side, alongside the glass). */
const ROLLER_BOX_DEPTH = 0.06;

/** Number of hatch ticks drawn inside the roller-shutter box. */
const ROLLER_HATCH_TICKS = 6;

/**
 * Thickness of the open door-leaf panel (the thin box), as a fraction of the
 * wall thickness. The panel is a thin rectangle standing perpendicular to the
 * wall; its length is the opening width and its thickness this fraction.
 */
const DOOR_PANEL_THICKNESS_FRACTION = 0.05;

/**
 * Build the glyph geometry for one opening: `segments` is a flat array of
 * `[x,y,z]` pairs (every 6 numbers = one segment) in world XY at `z`; `arcs`
 * lists the true circular arcs whose chord tessellation is embedded in
 * `segments` — see {@link OpeningGlyphArc}. Any glyph part drawn as a
 * circular arc should record itself there so exports emit it as a real arc.
 */
export function buildOpeningGlyph(input: OpeningGlyphInput, z: number): { segments: number[]; arcs: OpeningGlyphArc[] } {
  const { frame, kind } = input;
  if (frame.halfWidth <= 1e-4) return { segments: [], arcs: [] };
  const customType = customGlyphTypeOf(input);
  if (customType) return { segments: buildCustomOpeningGlyph(input, customType, z), arcs: [] };
  if (isBentOpeningFrame(frame)) {
    // Curved wall: draw in the local frame, then bend it along the wall. True
    // arcs don't survive the bend — exports use the chords.
    const local = buildOpeningGlyph({ ...input, frame: localOpeningFrame(frame.halfWidth) }, 0);
    return { segments: placeOpeningSegments(local.segments, frame, z), arcs: [] };
  }
  if (kind === OpeningKind.WINDOW) return { segments: buildWindowGlyph(input, z), arcs: [] };
  const arcs: OpeningGlyphArc[] = [];
  const segments = buildDoorGlyph(input, z, arcs);
  return { segments, arcs };
}

/**
 * Organisation door / window: its drawing (hinge at −u, opening toward +n)
 * mirrored for this opening's hinge / side, mapped onto the wall. A type the
 * library doesn't hold (not loaded, deleted) draws a dashed box in the gap.
 */
function buildCustomOpeningGlyph(input: OpeningGlyphInput, typeId: string, z: number): number[] {
  const { frame } = input;
  const type = getCustomOpeningType(typeId);
  if (type) return buildCustomOpeningSymbol(type.spec, input, z);
  const out: number[] = [];
  const local = localOpeningFrame(frame.halfWidth);
  const half = Math.max(0.02, input.wallThickness / 2);
  pushDashedLine(out, local, 0, -frame.halfWidth, frame.halfWidth, half, 6);
  pushDashedLine(out, local, 0, -frame.halfWidth, frame.halfWidth, -half, 6);
  return placeOpeningSegments(out, frame, z);
}

/**
 * A door / window spec drawn in an opening: evaluated at the opening's width,
 * wall thickness, height and params, mirrored for its hinge / side, mapped
 * onto the wall. (The Object Editor's Test mode draws its draft with it.)
 */
export function buildCustomOpeningSymbol(spec: ObjectDefinitionSpec, input: OpeningGlyphInput, z: number, layer: ObjectShapeLayer = 'glyph', dashedOut?: number[]): number[] {
  const localDashed: number[] | undefined = dashedOut ? [] : undefined;
  const local = evaluateCustomOpeningLocal(spec, { width: input.frame.halfWidth * 2, wall: input.wallThickness, height: input.height, params: input.customParams, dashedOut: localDashed }, layer);
  // Windows have no hinge: only a door's is mirrored.
  const mirrorU = input.kind === OpeningKind.DOOR && !input.hingeAtStart;
  mirrorOpeningSegments(local, mirrorU, !input.swingPositive);
  if (localDashed && dashedOut) placeOpeningSegments(mirrorOpeningSegments(localDashed, mirrorU, !input.swingPositive), input.frame, z, dashedOut);
  return placeOpeningSegments(local, input.frame, z);
}

/** Push a segment from a→b at height z into `out`. */
function pushSegment(out: number[], a: Pt2, b: Pt2, z: number): void {
  out.push(a.x, a.y, z, b.x, b.y, z);
}

/** Point at `center + u·wallDir + n·wallNorm`. */
function at(frame: OpeningFrame, u: number, n: number): Pt2 {
  return {
    x: frame.center.x + u * frame.wallDir.x + n * frame.wallNorm.x,
    y: frame.center.y + u * frame.wallDir.y + n * frame.wallNorm.y,
  };
}

/**
 * Window: a pair of lines parallel to the wall axis spanning the opening, inset
 * by a fixed thin amount on each side of the wall centreline (the classic
 * double-line glass symbol), plus short jamb lines closing the ends. A rollable
 * (roller-shutter) window adds a hatched box on the swing side, alongside the
 * glass.
 */
function buildWindowGlyph(input: OpeningGlyphInput, z: number): number[] {
  const { frame } = input;
  const out: number[] = [];
  const w = frame.halfWidth;
  const inset = WINDOW_HALF_THICKNESS; // two glass lines ~5 cm apart, centred
  for (const n of [inset, -inset]) {
    pushSegment(out, at(frame, -w, n), at(frame, w, n), z);
  }
  // Jambs: close the glass lines at both ends of the opening.
  pushSegment(out, at(frame, -w, inset), at(frame, -w, -inset), z);
  pushSegment(out, at(frame, w, inset), at(frame, w, -inset), z);

  if (input.rollable) {
    // Roller-shutter box: a thin rectangle along the full opening width on the
    // chosen side (swingPositive → +wallNorm), just outside the glass, with a
    // few hatch ticks to read as a shutter.
    const side = input.swingPositive ? 1 : -1;
    const nNear = side * inset; // box inner edge sits on the outer glass line
    const nFar = side * (inset + ROLLER_BOX_DEPTH);
    pushSegment(out, at(frame, -w, nNear), at(frame, w, nNear), z);
    pushSegment(out, at(frame, -w, nFar), at(frame, w, nFar), z);
    pushSegment(out, at(frame, -w, nNear), at(frame, -w, nFar), z);
    pushSegment(out, at(frame, w, nNear), at(frame, w, nFar), z);
    for (let i = 1; i < ROLLER_HATCH_TICKS; i++) {
      const u = -w + (2 * w * i) / ROLLER_HATCH_TICKS;
      pushSegment(out, at(frame, u, nNear), at(frame, u, nFar), z);
    }
  }
  return out;
}

/**
 * Door: dispatches on {@link DoorType} to the matching floor-plan glyph. The
 * SINGLE / DOUBLE swing leaves use `hingeAtStart` (−w / +w along the wall) and
 * `swingPositive` (±wallNorm). Sliding/pocket types ignore the arc but use
 * `swingPositive` for which side the panel sits / slides toward and
 * `hingeAtStart` for the slide direction.
 */
function buildDoorGlyph(input: OpeningGlyphInput, z: number, arcs: OpeningGlyphArc[]): number[] {
  const { frame, hingeAtStart, swingPositive, doorType } = input;
  const out: number[] = [];
  const sideSign = swingPositive ? 1 : -1;
  const slideDir = hingeAtStart ? -1 : 1; // along-wall direction the panel retracts
  const w = frame.halfWidth;

  switch (doorType) {
    case DoorType.OPEN_DOORWAY:
      // Just the gap — no leaf, no arc. (The cutout already breaks the wall.)
      break;

    case DoorType.DOUBLE:
      // Two leaves, each hinged at an opposite jamb, swinging the same side,
      // each spanning half the opening so the tips meet at the centre.
      buildDoorLeaf(input, out, arcs, z, -w, 0, sideSign);
      buildDoorLeaf(input, out, arcs, z, w, 0, sideSign);
      break;

    case DoorType.BIFOLD:
      // Folding leaf: two half-width panels peaked away from the wall, hinged at
      // the jamb and the centre (the classic bifold "zig" — two peaks for a pair
      // of bifolds). Drawn as a polyline jamb→peak→centre→peak→jamb.
      buildBifoldGlyph(input, out, z, sideSign);
      break;

    case DoorType.SLIDING:
      // Single panel parallel to the wall, offset to the swing side, with a
      // slide-direction arrow.
      buildSlidingGlyph(input, out, z, sideSign, slideDir, { panels: 1, surface: false });
      break;

    case DoorType.BYPASS_SLIDING:
      // Two overlapping panels on slightly offset tracks, with a double arrow.
      buildSlidingGlyph(input, out, z, sideSign, slideDir, { panels: 2, surface: false });
      break;

    case DoorType.SURFACE_SLIDING:
      // Panel mounted on the wall surface, shown with a dashed track above it
      // and a slide arrow.
      buildSlidingGlyph(input, out, z, sideSign, slideDir, { panels: 1, surface: true });
      break;

    case DoorType.POCKET:
      // Leaf slides into the wall: a panel spanning the opening with a dashed
      // "pocket" rectangle extending into the wall toward the slide side.
      buildPocketGlyph(input, out, z, sideSign, slideDir);
      break;

    case DoorType.CLOSET:
      // Closet / bypass-pair: two panels meeting at the centre, each drawn as a
      // thin leaf with a shallow arc — like a double door with a flatter swing.
      buildDoorLeaf(input, out, arcs, z, -w, 0, sideSign);
      buildDoorLeaf(input, out, arcs, z, w, 0, sideSign);
      break;

    case DoorType.SINGLE:
    default: {
      const hingeU = hingeAtStart ? -w : w;
      const latchU = hingeAtStart ? w : -w;
      buildDoorLeaf(input, out, arcs, z, hingeU, latchU, sideSign);
      break;
    }
  }
  return out;
}

/**
 * Bifold door: a pair of folding leaves. Each half of the opening folds into a
 * shallow peak pushed `peakDepth` off the wall toward `sideSign`, drawn as a
 * polyline jamb → peak → centre for each half (two peaks total).
 */
function buildBifoldGlyph(input: OpeningGlyphInput, out: number[], z: number, sideSign: number): void {
  const { frame } = input;
  const w = frame.halfWidth;
  const peakDepth = sideSign * w * 0.6;
  // Left half: −w → peak(−w/2) → 0. Right half: 0 → peak(+w/2) → +w.
  const pts: Pt2[] = [at(frame, -w, 0), at(frame, -w / 2, peakDepth), at(frame, 0, 0), at(frame, w / 2, peakDepth), at(frame, w, 0)];
  for (let i = 0; i + 1 < pts.length; i++) pushSegment(out, pts[i], pts[i + 1], z);
}

/**
 * Sliding-family glyph (sliding / bypass / surface), matching the standard
 * floor-plan symbols. All draw TWO overlapping panel boxes on slightly offset
 * tracks (each covering a bit more than half the opening, meeting at the
 * centre), with the slide arrow drawn BELOW the boxes (offset to −sideSign):
 *  - SLIDING: two boxes + a single directional slide arrow below.
 *  - BYPASS_SLIDING: two boxes + a double-ended arrow below (both panels slide).
 *  - SURFACE_SLIDING: like SLIDING, plus a dashed track line behind the boxes.
 */
function buildSlidingGlyph(input: OpeningGlyphInput, out: number[], z: number, sideSign: number, slideDir: number, opts: { panels: number; surface: boolean }): void {
  const { frame } = input;
  const w = frame.halfWidth;
  const panelHalf = (input.wallThickness * DOOR_PANEL_THICKNESS_FRACTION) / 2;

  // Two overlapping panels on offset tracks, on the swing side of the wall.
  const trackOffset = panelHalf * 1.2;
  const overlap = w * 0.2;
  pushPanel(out, frame, z, -w, overlap, sideSign * (panelHalf - trackOffset), panelHalf);
  pushPanel(out, frame, z, -overlap, w, sideSign * (panelHalf + trackOffset), panelHalf);

  if (opts.surface) {
    // Dashed track line behind the panels (further out on the swing side).
    pushDashedLine(out, frame, z, -w, w, sideSign * (panelHalf + trackOffset * 2), 8);
  }

  // Slide arrow BELOW the boxes — on the opposite side of the wall centreline
  // from the panels, so it doesn't overlap them.
  const arrowN = -sideSign * Math.max(w * 0.22, panelHalf * 2);
  if (opts.panels >= 2) {
    pushDoubleArrow(out, frame, z, -w * 0.4, w * 0.4, arrowN, w * 0.1);
  } else {
    pushArrow(out, frame, z, -slideDir * w * 0.5, slideDir * w * 0.5, arrowN, w * 0.1);
  }
}

/** Push a double-ended arrow along the wall from u0→u1 at offset `n`, with
 * chevron heads of `headSize` at both ends. */
function pushDoubleArrow(out: number[], frame: OpeningFrame, z: number, u0: number, u1: number, n: number, headSize: number): void {
  pushSegment(out, at(frame, u0, n), at(frame, u1, n), z);
  pushSegment(out, at(frame, u0, n), at(frame, u0 + headSize, n + headSize), z);
  pushSegment(out, at(frame, u0, n), at(frame, u0 + headSize, n - headSize), z);
  pushSegment(out, at(frame, u1, n), at(frame, u1 - headSize, n + headSize), z);
  pushSegment(out, at(frame, u1, n), at(frame, u1 - headSize, n - headSize), z);
}

/**
 * Pocket door: a panel spanning the opening, plus a dashed rectangle (the wall
 * pocket the leaf retracts into) extending one opening-width toward `slideDir`.
 */
function buildPocketGlyph(input: OpeningGlyphInput, out: number[], z: number, sideSign: number, slideDir: number): void {
  const { frame } = input;
  const w = frame.halfWidth;
  const panelHalf = (input.wallThickness * DOOR_PANEL_THICKNESS_FRACTION) / 2;
  const n = sideSign * panelHalf;
  // Visible leaf across the opening.
  pushPanel(out, frame, z, -w, w, n, panelHalf);
  // Dashed pocket extending into the wall on the slide side.
  const pocketStart = slideDir < 0 ? -w - 2 * w : w;
  const pocketEnd = slideDir < 0 ? -w : w + 2 * w;
  pushDashedLine(out, frame, z, pocketStart, pocketEnd, n - panelHalf, 8);
  pushDashedLine(out, frame, z, pocketStart, pocketEnd, n + panelHalf, 8);
  pushSegment(out, at(frame, slideDir < 0 ? pocketStart : pocketEnd, n - panelHalf), at(frame, slideDir < 0 ? pocketStart : pocketEnd, n + panelHalf), z);
}

/** Push a thin rectangular panel parallel to the wall: spanning [u0,u1] along
 * the wall axis, centred at `nCenter` across it, with half-thickness `nHalf`. */
function pushPanel(out: number[], frame: OpeningFrame, z: number, u0: number, u1: number, nCenter: number, nHalf: number): void {
  const a = at(frame, u0, nCenter - nHalf);
  const b = at(frame, u1, nCenter - nHalf);
  const c = at(frame, u1, nCenter + nHalf);
  const d = at(frame, u0, nCenter + nHalf);
  pushSegment(out, a, b, z);
  pushSegment(out, b, c, z);
  pushSegment(out, c, d, z);
  pushSegment(out, d, a, z);
}

/** Push a dashed line parallel to the wall from u0→u1 at offset `n`, `dashes`
 * segments with equal gaps. */
function pushDashedLine(out: number[], frame: OpeningFrame, z: number, u0: number, u1: number, n: number, dashes: number): void {
  const step = (u1 - u0) / (dashes * 2 - 1);
  for (let i = 0; i < dashes; i++) {
    const a = u0 + step * (2 * i);
    const b = a + step;
    pushSegment(out, at(frame, a, n), at(frame, b, n), z);
  }
}

/** Push a slide arrow along the wall from `uTail`→`uHead` at offset `n`, with a
 * chevron head of size `headSize`. */
function pushArrow(out: number[], frame: OpeningFrame, z: number, uTail: number, uHead: number, n: number, headSize: number): void {
  pushSegment(out, at(frame, uTail, n), at(frame, uHead, n), z);
  const dir = Math.sign(uHead - uTail) || 1;
  pushSegment(out, at(frame, uHead, n), at(frame, uHead - dir * headSize, n + headSize), z);
  pushSegment(out, at(frame, uHead, n), at(frame, uHead - dir * headSize, n - headSize), z);
}

/**
 * One door leaf: a thin panel box hinged at `hingeU` (at the wall plane),
 * swinging open toward `sideSign·wallNorm`, with a quarter-circle arc from the
 * open tip to `latchU` (the closed position along the wall). The leaf length is
 * the hinge→latch distance, so the arc lands exactly on the latch jamb.
 */
function buildDoorLeaf(input: OpeningGlyphInput, out: number[], arcs: OpeningGlyphArc[], z: number, hingeU: number, latchU: number, sideSign: number): void {
  const { frame } = input;
  const dirToLatch = Math.sign(latchU - hingeU) || 1;
  const leafLength = Math.abs(latchU - hingeU);
  if (leafLength <= 1e-4) return;

  // Open door-leaf box: THIN along the wall axis (panel thickness), LONG across
  // the wall (the leaf length). Corners in (u along wall, n across wall):
  const panelThickness = input.wallThickness * DOOR_PANEL_THICKNESS_FRACTION;
  const farU = hingeU + dirToLatch * panelThickness;
  const tipN = sideSign * leafLength;

  const c0 = at(frame, hingeU, 0);
  const c1 = at(frame, hingeU, tipN);
  const c2 = at(frame, farU, tipN);
  const c3 = at(frame, farU, 0);
  pushSegment(out, c0, c1, z);
  pushSegment(out, c1, c2, z);
  pushSegment(out, c2, c3, z);
  pushSegment(out, c3, c0, z);

  // Swing arc: quarter circle centred on the hinge, radius = leaf length, from
  // the open tip (90°) to the closed latch position (0°). The chords render
  // and hit-test; the true arc is recorded alongside for arc-capable exports.
  const segStart = out.length;
  let first: Pt2 | null = null;
  let prev: Pt2 | null = null;
  for (let i = 0; i <= ARC_SEGMENTS; i++) {
    const t = (Math.PI / 2) * (1 - i / ARC_SEGMENTS);
    const u = hingeU + dirToLatch * leafLength * Math.cos(t);
    const n = sideSign * leafLength * Math.sin(t);
    const pt = at(frame, u, n);
    if (prev) pushSegment(out, prev, pt, z);
    else first = pt;
    prev = pt;
  }
  if (first && prev) {
    // Signed CCW quarter-turn from the world winding of tip → latch about the
    // hinge; internal bulge = −tan(travel/4) (positive bows left).
    const hinge = at(frame, hingeU, 0);
    const cross = (first.x - hinge.x) * (prev.y - hinge.y) - (first.y - hinge.y) * (prev.x - hinge.x);
    const travel = (Math.sign(cross) || 1) * (Math.PI / 2);
    arcs.push({ start: first, end: prev, z, bulge: -Math.tan(travel / 4), segStart, segEnd: out.length });
  }
}
